import { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, Button } from '@bwoty-web/styleguide';
import LargePopup from '../LargePopup/LargePopup';

import styles from './DepartureFieldOverlay.scss';

function arrMatch(a1, a2) {
  if (a1.length !== a2.length) {
    return false;
  }

  for (let i = 0; i < a1.length; i += 1) {
    if (a1[i] !== a2[i]) {
      return false;
    }
  }

  return true;
}

export const DepartureFieldOverlay = ({
  departures,
  isOpen = false,
  onClose,
  onChange,
  selected,
  texts,
}) => {
  const [selectedDestinations, setSelected] = useState(selected);

  const combineDeparturesWithDuplicateName = () => {
    const combinedDepartureCodes = {};
    const uniqueDepartures = [];

    departures.forEach(({code, name}) => {
      if (!combinedDepartureCodes[name]) {
        combinedDepartureCodes[name] = [code];
        uniqueDepartures.push({name, code});
      } else {
        combinedDepartureCodes[name].push(code);
      }
    });

    return { combinedDepartureCodes, uniqueDepartures };
  };

  const { combinedDepartureCodes, uniqueDepartures } = combineDeparturesWithDuplicateName();

  const handleClose = useCallback(() => {
    if (isOpen) {
      onChange(selectedDestinations);
      onClose();
    }
  }, [selectedDestinations, onClose, isOpen]);

  const handleSelect = useCallback(
    (e) => {
      const { checked, name } = e.target;
      const selectedDepartureCodes = combinedDepartureCodes[name];
      if (checked) {
        setSelected([...selectedDestinations, ...selectedDepartureCodes]);
      } else {
        setSelected(selectedDestinations.filter((s) => !selectedDepartureCodes.includes(s)));
      }
    },
    [selectedDestinations],
  );

  // update internal state when we open, this is to catch changes to destination outside this component.
  useEffect(() => {
    if (isOpen) {
      setSelected(selected);
    }
  }, [isOpen]);

  // trigger change when we get closed by prop. This will trigger onChange when user clicks outside the dropdown.
  useEffect(() => {
    if (
      !isOpen &&
      selectedDestinations &&
      selected &&
      selectedDestinations.length &&
      !arrMatch(selectedDestinations, selected)
    ) {
      onChange(selectedDestinations);
    }
  }, [isOpen, selectedDestinations, selected]);
  return (
    <LargePopup header={texts.departureSelectHeading} onClose={handleClose} isOpen={isOpen}>
      <div className={styles.root}>
        {uniqueDepartures.map((d) => {
          return (
            <div key={d.code} className={styles.item}>
              <Checkbox
                className={styles.checkbox}
                checked={selectedDestinations.includes(d.code)}
                onChange={handleSelect}
                value={d.code}
                name={d.name}
                uniqueId={`lms-destination--${d.code}`}
                data-test="lms-destination"
                fullWidthLabel
              >
                {d.name}
              </Checkbox>
            </div>
          );
        })}
      </div>
      <div className={styles.ctaWrapper}>
        <Button
          theme="primary"
          className={styles.button}
          onClick={handleClose}
          data-test="lms-destination-cta"
          rel="nofollow"
        >
          {texts.change}
        </Button>
      </div>
    </LargePopup>
  );
};

DepartureFieldOverlay.propTypes = {
  departures: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  selected: PropTypes.array,
  texts: PropTypes.object,
};
